import ActionsContainer from "./ActionsContainer";
import Button from "@mui/material/Button";
import ToolbarContainer from "./ToolbarContainer";
import React, { useMemo, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import AppBar from "./AppBar";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import {
  ACTIONS,
  getPartRefById,
  getUserImageResourcesByPartRef,
  getUserTextResourcesByPartRef,
  templateIsComplete,
} from "../../../../../Providers/TemplateInstanceProvider";
import { Trans, useTranslation } from "react-i18next";
import TemplatePageList from "./TemplatePageList";
import Icon from "@mui/material/Icon";
import { Bubble, BubbleList } from "./Bubble";
import TemplateContainer from "./TemplateContainer";
import PreviewDialog from "./PreviewDialog";
import { useHistory } from "react-router";
import ExitDialog from "./ExitDialog";
import ToolbarContent from "./ToolbarContent";
import EditPhotosIcon from "../../../../../components/Icons/EditPhotosIcon";
import PreviewTemplateIcon from "../../../../../components/Icons/PreviewTemplateIcon";
import EditTextIcon from "../../../../../components/Icons/EditTextIcon";
import ChangeTemplateIcon from "../../../../../components/Icons/ChangeTemplateIcon";
import { usePortal } from "../../../../../Providers/PortalProvider";
import AddTemplateProductToCartWarningDialog from "../../AddTemplateProductToCartWarningDialog";
import { useAddToCartUseBuyNow } from "@/react/Webshop/hooks/useAddToCartUseBuyNow";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
}));

const MainPage = ({
  templateInstance,
  changeMenu,
  mode,
  updateCart,
  handleGoBack,
  cartButtonLabel,
  allowTemplateChange,
}) => {
  const classes = useStyles();
  const [state, dispatch] = useTemplateInstance();
  const { t } = useTranslation();
  const [previewMode, setPreviewMode] = useState(false);
  const history = useHistory();
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);
  const [showAddToCartWarning, setShowAddToCartWarning] = useState(false);
  const { options, webshop } = usePortal();
  const useBuyNow = useAddToCartUseBuyNow();

  const textResources = useMemo(() => {
    const partRef = getPartRefById(state.currentPartRefId, state.template);
    return getUserTextResourcesByPartRef(partRef, state);
  }, [templateInstance, state]);

  const isComplete = useMemo(() => {
    return templateIsComplete(state);
  }, [state]);

  const showAddToCartWarningIfEnabled = () => {
    const warningEnabled = webshop.template.addToCartWarningEnabled;
    if (!warningEnabled) {
      return false;
    }

    if (
      sessionStorage.getItem("add_template_product_to_cart_warning_displayed")
    ) {
      return false;
    }

    sessionStorage.setItem(
      "add_template_product_to_cart_warning_displayed",
      "1",
    );

    setShowAddToCartWarning(true);

    return true;
  };

  const addToCart = () => {
    //const resourceList = getUserResourcesAsList(state);
    //console.log('Resource list', resourceList);
    // Check if we're done...

    if (showAddToCartWarningIfEnabled()) {
      return;
    }

    changeMenu("buy");
  };

  const handleUpdateCart = () => {
    const resources = Object.values(state.resources).filter(
      (resource) => resource.userSupplied,
    );
    updateCart(resources, state.year, state.template);
  };

  const onSelectImage = () => {
    const partRef = getPartRefById(state.currentPartRefId, state.template);
    const resources = getUserImageResourcesByPartRef(partRef, state);

    if (resources.length == 1) {
      dispatch({
        type: ACTIONS.SET_CURRENT_RESOURCE,
        id: resources[0].fullId,
      });
      changeMenu("image");
    } else {
      changeMenu("select-image");
    }
  };

  const onSelectText = () => {
    if (textResources.length == 1) {
      dispatch({
        type: ACTIONS.SET_CURRENT_RESOURCE,
        id: textResources[0].fullId,
      });
      changeMenu("text");
    } else {
      changeMenu("select-text");
    }
  };

  const handleUserResourceClick = (e, resource) => {
    dispatch({
      type: ACTIONS.SET_CURRENT_RESOURCE,
      id: resource.fullId,
    });
  };

  const localHandleGoBack = (e) => {
    e.stopPropagation();
    if (state.dirty) {
      setShowSaveChangesDialog(true);
    } else {
      doGoBack();
    }
  };

  const doGoBack = () => {
    if (handleGoBack) {
      handleGoBack();
    } else {
      history.goBack();
    }
  };

  console.log("Can change template", allowTemplateChange);

  return (
    <div className={classes.mainContainer}>
      <AppBar onCancel={localHandleGoBack} />

      <TemplateContainer
        handleUserImageClick={handleUserResourceClick}
        handleUserStringClick={handleUserResourceClick}
        dispatch={dispatch}
      />

      <TemplatePageList />

      <ToolbarContainer>
        <ToolbarContent>
          <BubbleList>
            <Bubble
              header={
                options.disableImageToolbox
                  ? t("Change photos")
                  : t("Change/edit photos")
              }
              content={<EditPhotosIcon />}
              onClick={onSelectImage}
            />
            {textResources.length > 0 && (
              <Bubble
                header={t("Edit text")}
                content={<EditTextIcon />}
                onClick={onSelectText}
              />
            )}
            {state.template.type == "CALENDAR" && (
              <Bubble
                header={t("Change year")}
                content={state.year}
                disabled={
                  state.template.supportedYears &&
                  state.template.supportedYears.length == 1
                }
                onClick={() => changeMenu("year")}
              />
            )}
            {allowTemplateChange && (
              <Bubble
                header={t("Change design")}
                content={<ChangeTemplateIcon />}
                //disabled={(!allowTemplateChange)}
                onClick={() => changeMenu("templates")}
              />
            )}
            <Bubble
              header={t("Preview")}
              content={<PreviewTemplateIcon />}
              onClick={() => {
                setPreviewMode(true);
              }}
            />
          </BubbleList>
        </ToolbarContent>
        <ActionsContainer>
          <Button color="primary" onClick={localHandleGoBack}>
            <Trans>Cancel</Trans>
          </Button>
          {mode == "update" && (
            <Button
              color="primary"
              onClick={handleUpdateCart}
              disabled={!isComplete}
            >
              {cartButtonLabel ? cartButtonLabel : <Trans>Update cart</Trans>}
            </Button>
          )}
          {mode != "update" && (
            <Button color="primary" onClick={addToCart} disabled={!isComplete}>
              {useBuyNow ? <Trans>Buy now</Trans> : <Trans>Add to cart</Trans>}
            </Button>
          )}
        </ActionsContainer>
      </ToolbarContainer>
      <ExitDialog
        open={showSaveChangesDialog}
        handleCancel={() => setShowSaveChangesDialog(false)}
        handleOk={doGoBack}
      />
      {previewMode && (
        <PreviewDialog
          templateInstance={state}
          onClose={() => setPreviewMode(false)}
        />
      )}
      {showAddToCartWarning && (
        <AddTemplateProductToCartWarningDialog
          handleCancel={() => setShowAddToCartWarning(false)}
          handleAddToCart={() => addToCart()}
        />
      )}
    </div>
  );
};

export default MainPage;
